import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../Modal';
import { MagnifyingGlassIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export interface LookupItem {
  id: number;
  nome: string;
  cbo?: string | null;
  descricao?: string | null;
}

interface SearchLookupModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  placeholder?: string;
  selectedId?: number | null;
  selectedName?: string | null;
  fetchData?: (params: { page: number; limit: number; search?: string }) => Promise<{
    data: LookupItem[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }>;
  staticItems?: LookupItem[];
  onSelect: (item: LookupItem) => void;
}

export default function SearchLookupModal({
  isOpen,
  onClose,
  title,
  subtitle,
  placeholder = 'Buscar por nome...',
  selectedId,
  selectedName,
  fetchData,
  staticItems,
  onSelect,
}: SearchLookupModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(8);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<LookupItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setDebouncedSearch('');
      setPage(1);
    }
  }, [isOpen]);

  // Load data
  const loadData = useCallback(async () => {
    if (!isOpen) return;

    if (fetchData) {
      setLoading(true);
      try {
        const res = await fetchData({ page, limit, search: debouncedSearch.trim() || undefined });
        setItems(res.data || []);
        setTotal(res.meta?.total || 0);
        setTotalPages(Math.max(1, res.meta?.totalPages || 1));
      } catch (err) {
        console.error('Erro ao buscar itens:', err);
        setItems([]);
        setTotal(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    } else if (staticItems) {
      // Local filtering and pagination
      const normalizedSearch = debouncedSearch.toLowerCase().trim();
      const filtered = staticItems
        .filter((item) => {
          const matchName = item.nome.toLowerCase().includes(normalizedSearch);
          const matchCbo = item.cbo ? item.cbo.toLowerCase().includes(normalizedSearch) : false;
          return matchName || matchCbo;
        })
        .sort((a, b) => a.nome.localeCompare(b.nome));

      const startIndex = (page - 1) * limit;
      setItems(filtered.slice(startIndex, startIndex + limit));
      setTotal(filtered.length);
      setTotalPages(Math.max(1, Math.ceil(filtered.length / limit)));
    }
  }, [isOpen, fetchData, staticItems, page, limit, debouncedSearch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleItemClick = (item: LookupItem) => {
    onSelect(item);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="lg"
      className="max-w-2xl"
    >
      <div className="flex flex-col space-y-4">
        {subtitle && (
          <p className="text-sm text-gray-500 -mt-2">
            {subtitle}
          </p>
        )}

        {/* Input de Busca */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={placeholder}
            autoFocus
            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:bg-white transition-all"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-xs text-gray-400 hover:text-gray-600"
            >
              Limpar
            </button>
          )}
        </div>

        {/* Informações da Busca e Total */}
        <div className="flex items-center justify-between text-xs text-gray-500 px-1">
          <span>
            {loading ? 'Buscando...' : `${total} registro(s) encontrado(s) por ordem alfabética`}
          </span>
          {totalPages > 1 && (
            <span>
              Página {page} de {totalPages}
            </span>
          )}
        </div>

        {/* Lista de Itens */}
        <div className="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100 bg-white min-h-[280px] max-h-[360px] overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 space-y-3">
              <div className="w-8 h-8 border-3 border-green-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-gray-500 font-medium">Carregando dados da empresa...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                <MagnifyingGlassIcon className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-semibold text-gray-700">Nenhum resultado encontrado</p>
              <p className="text-xs text-gray-500 mt-1 max-w-sm">
                {searchTerm
                  ? `Não encontramos registros correspondentes a "${searchTerm}". Tente outro termo.`
                  : 'Nenhum registro cadastrado para esta empresa no momento.'}
              </p>
            </div>
          ) : (
            items.map((item) => {
              const isSelected =
                (selectedId && item.id === selectedId) ||
                (selectedName && item.nome.toLowerCase() === selectedName.toLowerCase());

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleItemClick(item)}
                  className={`w-full text-left px-4 py-3.5 flex items-center justify-between transition-all hover:bg-green-50/70 group ${
                    isSelected ? 'bg-green-50 border-l-4 border-green-600 pl-3' : ''
                  }`}
                >
                  <div className="flex flex-col pr-4 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-medium ${isSelected ? 'text-green-900 font-bold' : 'text-gray-900 group-hover:text-green-800'}`}>
                        {item.nome}
                      </span>
                      {item.cbo && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono font-medium bg-slate-100 text-slate-700 border border-slate-200">
                          CBO: {item.cbo}
                        </span>
                      )}
                    </div>
                    {item.descricao && (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                        {item.descricao}
                      </p>
                    )}
                  </div>

                  <div className="shrink-0 flex items-center">
                    {isSelected ? (
                      <span className="flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2.5 py-1 rounded-full">
                        <CheckIcon className="w-3.5 h-3.5" />
                        Selecionado
                      </span>
                    ) : (
                      <span className="text-xs font-medium text-gray-400 group-hover:text-green-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                        Selecionar
                        <ChevronRightIcon className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              Anterior
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .map((p, idx, arr) => {
                  const prev = arr[idx - 1];
                  const showEllipsis = prev && p - prev > 1;

                  return (
                    <React.Fragment key={p}>
                      {showEllipsis && <span className="text-xs text-gray-400 px-1">...</span>}
                      <button
                        type="button"
                        onClick={() => setPage(p)}
                        className={`w-7 h-7 text-xs rounded-lg font-medium transition-colors ${
                          p === page
                            ? 'bg-green-600 text-white font-bold'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  );
                })}
            </div>

            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Próxima
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
